/**
 * Shared hover chart for every Palworld price and index graph.
 *
 * Industry-standard reading model (TCGplayer / Card Ladder style): a gridded
 * plot with labelled value and date axes, soft gradient fills under each line,
 * clean turning-point dots, and a crosshair tooltip that prints the exact value
 * for the hovered day. No numbers are stamped on the line itself.
 */
import { useId, useMemo, useRef, useState, type ReactNode } from "react";
import { extrema, isFlatSeries } from "@/lib/chart-extrema";

export interface HoverSeries {
  id: string;
  label: string;
  color: string;
  /** One value per label. `undefined` marks a day with no recorded price (line breaks there). */
  values: (number | undefined)[];
}

/** Nice-looking tick values across a range. */
function ticks(min: number, max: number, count = 4): number[] {
  if (!(max > min)) return [min];
  const step = (max - min) / count;
  return Array.from({ length: count + 1 }, (_, i) => min + step * i);
}

export function HoverChart({
  labels,
  series,
  baseline,
  format,
  ariaLabel,
  area = false,
  markTurns = true,
  tooltipExtra,
  columns,
  height = 260,
}: {
  /** X axis labels (one per point), used on the date axis and in the tooltip. */
  labels: string[];
  series: HoverSeries[];
  /** Optional dashed reference level (index base of 1,000, for example). */
  baseline?: number;
  format: (v: number) => string;
  ariaLabel: string;
  area?: boolean;
  markTurns?: boolean;
  /** Extra tooltip rows for the hovered index (change, profit, ROI, …). */
  tooltipExtra?: (i: number) => ReactNode;
  /** Optional volume columns (TCGplayer-style bars) under the price line. */
  columns?: { label: string; values: (number | undefined)[]; color?: string };
  height?: number;
}) {
  const w = 1000;
  const h = height;
  const padTop = 14;
  const padBottom = 26;
  const padLeft = 62;
  const padRight = 10;
  const wrap = useRef<HTMLDivElement>(null);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [hover, setHover] = useState<number | null>(null);

  const all = series.flatMap((s) => s.values).filter((v): v is number => typeof v === "number");
  const n = Math.max(1, ...series.map((s) => s.values.length));
  const { min, max } = useMemo(() => {
    if (!all.length) return { min: 0, max: 1 };
    const lo = Math.min(...all, baseline ?? Infinity);
    const hi = Math.max(...all, baseline ?? -Infinity);
    const pad = (hi - lo || Math.abs(hi) * 0.04 || 1) * 0.08;
    return { min: lo - pad, max: hi + pad };
  }, [all.join(","), baseline]);
  if (!all.length) return null;

  const span = max - min || 1;
  const plotW = w - padLeft - padRight;
  const plotH = h - padTop - padBottom;
  const colMax = columns ? Math.max(1, ...columns.values.map((v) => v ?? 0)) : 1;
  const colBand = columns ? plotH * 0.28 : 0;
  const colW = Math.max(2, (plotW / Math.max(1, n)) * 0.55);
  const colColor = columns?.color ?? "currentColor";
  const x = (i: number) => padLeft + (i / Math.max(1, n - 1)) * plotW;
  const y = (v: number) => padTop + plotH - ((v - min) / span) * plotH;

  const onMove = (clientX: number) => {
    const rect = wrap.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const scale = rect.width / w;
    const px = (clientX - rect.left) / scale;
    const frac = Math.min(1, Math.max(0, (px - padLeft) / plotW));
    setHover(Math.round(frac * (n - 1)));
  };

  const gridValues = ticks(min, max, 4);
  const xTickIdx = [...new Set([0, Math.floor((n - 1) / 2), n - 1])].filter((i) => i >= 0 && i < n);
  /** Vertical gridlines so short or flat series never read as an empty panel. */
  const xGridIdx = [...new Set(Array.from({ length: 7 }, (_, k) => Math.round((k / 6) * (n - 1))))].filter(
    (i) => i >= 0 && i < n,
  );
  const pctLeft = hover === null ? 0 : (x(hover) / w) * 100;
  const tipShift = pctLeft < 20 ? "translate-x-0" : pctLeft > 80 ? "-translate-x-full" : "-translate-x-1/2";

  return (
    <div
      ref={wrap}
      className="relative touch-pan-y select-none"
      onPointerMove={(e) => onMove(e.clientX)}
      onPointerDown={(e) => onMove(e.clientX)}
      onPointerLeave={() => setHover(null)}
    >
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" role="img" aria-label={ariaLabel}>
        <defs>
          {series.map((s) => (
            <linearGradient key={`g-${s.id}`} id={`fill-${uid}-${s.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.32" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0.02" />
            </linearGradient>
          ))}
        </defs>

        {/* value grid + axis */}
        {gridValues.map((v) => (
          <g key={`grid-${v}`}>
            <line
              x1={padLeft}
              y1={y(v)}
              x2={w - padRight}
              y2={y(v)}
              stroke="currentColor"
              strokeOpacity="0.09"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            <text x={padLeft - 8} y={y(v) + 4} textAnchor="end" className="fill-current text-[11px] opacity-45">
              {format(v)}
            </text>
          </g>
        ))}

        {/* vertical grid */}
        {xGridIdx.map((i) => (
          <line
            key={`vg-${i}`}
            x1={x(i)}
            y1={padTop}
            x2={x(i)}
            y2={padTop + plotH}
            stroke="currentColor"
            strokeOpacity="0.06"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* date axis */}
        {xTickIdx.map((i) => (
          <text
            key={`xt-${i}`}
            x={x(i)}
            y={h - 8}
            textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
            className="fill-current text-[11px] opacity-45"
          >
            {labels[i] ?? ""}
          </text>
        ))}

        {baseline !== undefined ? (
          <line
            x1={padLeft}
            y1={y(baseline)}
            x2={w - padRight}
            y2={y(baseline)}
            stroke="currentColor"
            strokeOpacity="0.3"
            strokeDasharray="6 6"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}

        {columns ? (
          <g aria-hidden="true">
            {columns.values.map((v, i) =>
              v === undefined || v <= 0 ? null : (
                <rect
                  key={`col-${i}`}
                  x={x(i) - colW / 2}
                  y={padTop + plotH - (v / colMax) * colBand}
                  width={colW}
                  height={(v / colMax) * colBand}
                  fill={colColor}
                  opacity={hover === i ? 0.45 : 0.2}
                  rx="1"
                />
              ),
            )}
          </g>
        ) : null}

        {series.map((s) => {
          // Split on missing days so a partially recorded series draws every
          // segment it does have instead of one line that stops early.
          const segments: { i: number; v: number }[][] = [];
          let current: { i: number; v: number }[] = [];
          s.values.forEach((v, i) => {
            if (typeof v === "number") {
              current.push({ i, v });
            } else if (current.length) {
              segments.push(current);
              current = [];
            }
          });
          if (current.length) segments.push(current);

          const defined = s.values
            .map((v, i) => ({ v, i }))
            .filter((p): p is { v: number; i: number } => typeof p.v === "number");
          const definedValues = defined.map((p) => p.v);
          const turns =
            markTurns && definedValues.length > 2 && !isFlatSeries(definedValues)
              ? extrema(definedValues, 0.08).map((t) => ({ ...t, index: defined[t.index]!.i, value: definedValues[t.index]! }))
              : [];
          return (
            <g key={s.id}>
              {segments.map((seg, k) => {
                const pts = seg.map((p) => `${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`);
                return (
                  <g key={`${s.id}-seg-${k}`}>
                    {area && seg.length > 1 ? (
                      <polygon
                        points={`${x(seg[0]!.i)},${padTop + plotH} ${pts.join(" ")} ${x(seg[seg.length - 1]!.i)},${padTop + plotH}`}
                        fill={`url(#fill-${uid}-${s.id})`}
                        stroke="none"
                      />
                    ) : null}
                    {seg.length > 1 ? (
                      <polyline
                        points={pts.join(" ")}
                        fill="none"
                        stroke={s.color}
                        strokeWidth="2.4"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                      />
                    ) : (
                      <circle cx={x(seg[0]!.i)} cy={y(seg[0]!.v)} r="3" fill={s.color} />
                    )}
                  </g>
                );
              })}
              {turns.map((t) => (
                <circle
                  key={`${s.id}-${t.index}`}
                  cx={x(t.index)}
                  cy={y(t.value)}
                  r={t.kind === "peak" || t.kind === "trough" ? 3.4 : 2.6}
                  fill={s.color}
                  opacity="0.9"
                />
              ))}
            </g>
          );
        })}

        {hover !== null ? (
          <g>
            <line
              x1={x(hover)}
              y1={padTop}
              x2={x(hover)}
              y2={padTop + plotH}
              stroke="currentColor"
              strokeOpacity="0.4"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            {series.map((s) =>
              s.values[hover] === undefined ? null : (
                <circle
                  key={`dot-${s.id}`}
                  cx={x(hover)}
                  cy={y(s.values[hover])}
                  r="5"
                  fill={s.color}
                  stroke="var(--color-background)"
                  strokeWidth="2"
                />
              ),
            )}
          </g>
        ) : null}
      </svg>

      {hover !== null ? (
        <div
          className={`pointer-events-none absolute top-1 z-10 ${tipShift} rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl`}
          style={{ left: `${pctLeft}%` }}
          role="status"
        >
          <p className="font-semibold text-foreground">{labels[hover] ?? ""}</p>
          <ul className="mt-1 space-y-0.5">
            {series.map((s) =>
              s.values[hover] === undefined ? null : (
                <li key={`tip-${s.id}`} className="flex items-center gap-2 whitespace-nowrap">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} aria-hidden="true" />
                  <span className="text-muted-foreground">{s.label}</span>
                  <strong className="ml-auto tabular-nums text-foreground">{format(s.values[hover])}</strong>
                </li>
              ),
            )}
          </ul>
          {columns && (columns.values[hover] ?? 0) > 0 ? (
            <p className="mt-1 whitespace-nowrap text-[11px] text-muted-foreground">
              {columns.label}: <strong className="tabular-nums text-foreground">{columns.values[hover]}</strong>
            </p>
          ) : null}
          {tooltipExtra ? <div className="mt-1 border-t border-border/60 pt-1">{tooltipExtra(hover)}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
